import { Link } from 'react-router-dom';

import './AdminPages.css';
import './AdminHome.css';

/**
 * The admin console's home screen: one place to reach every admin tool.
 *
 * This replaced Compliance as the landing screen. Compliance is still the most
 * operationally urgent queue and is still one click away, but landing straight on it
 * meant the console had no home, and the tools that are not in the left nav's muscle
 * memory got reached by typing URLs or by going out to the public site and back in.
 *
 * PURELY NAVIGATION. No counts, no queue depths, no fetches. Every destination here
 * already loads its own data, and a tile showing "3 pending" would be a second, slower
 * source of truth that is wrong for as long as the page sits open. The tiles say where
 * a thing is and what it does, and the screen itself says how many.
 *
 * GROUPED BY WHAT THE ADMIN IS DOING, not by which task built it. Approvals sit
 * together because they are worked in one sitting; the investment tools sit in the order
 * a raise actually moves (open it, decide who may see it, record what closed). A flat
 * list of eleven links would have been the same problem the left nav already has.
 */

interface Tile {
  to: string;
  label: string;
  description: string;
  /**
   * Leaves the admin section. Called out, because it is a surprise otherwise.
   *
   * Nothing sets it today: task 7 gave the shared Event screens admin-native routes, so
   * the one tile that used to jump into the Organizer portal no longer does. The flag
   * stays because the next tile that genuinely leaves should still say so.
   */
  external?: boolean;
}

interface Group {
  title: string;
  blurb: string;
  tiles: Tile[];
}

const GROUPS: Group[] = [
  {
    title: 'Events',
    blurb: 'Put a new event into the system.',
    tiles: [
      {
        to: '/admin/events',
        label: 'All events',
        description: 'Every event on the platform, drafts included.',
      },
      {
        to: '/admin/events/new',
        label: 'Create event',
        // Still the one real wizard — the same component the organizer portal uses, now
        // mounted inside the admin shell. It used to be linked at /organizer/events/new,
        // which threw the admin out of this console on the first click.
        description: 'The event is created under your own account, without leaving Admin.',
      },
    ],
  },
  {
    title: 'Approvals and review',
    blurb: 'Queues that gate what becomes visible or publishable.',
    tiles: [
      {
        to: '/admin/compliance',
        label: 'Compliance review',
        description: 'Approve the checklist that blocks an event from publishing.',
      },
      {
        to: '/admin/artists',
        label: 'Artist approvals',
        description: 'Decide which artist profiles appear in the public directory.',
      },
      {
        to: '/admin/venues',
        label: 'Venue approvals',
        description: 'Decide which venues are listed and can take booking requests.',
      },
      {
        to: '/admin/rentals',
        label: 'Rental pipeline',
        description: 'Vyrtual booking queue, calendar, insurance audit and rate card.',
      },
      {
        to: '/admin/investor-approvals',
        label: 'Investor approvals',
        description: 'Accreditation applications, and inquiries from the offering page.',
      },
    ],
  },
  {
    title: 'Investment',
    blurb: 'In the order a raise moves: open it, decide who may see it, record what closed.',
    tiles: [
      {
        to: '/admin/opportunities',
        label: 'Investment opportunities',
        description: 'Create and manage 506(b) and 506(c) raises against an event.',
      },
      {
        to: '/admin/network',
        label: 'Network tagging',
        description: 'Record the relationships that qualify an investor for a 506(b) raise.',
      },
      {
        to: '/admin/investments',
        label: 'Investments',
        description: 'Record a closing, then confirm funds once the transfer lands.',
      },
      {
        to: '/admin/spvs',
        label: 'SPV payouts',
        description: 'Onboard the entity that receives net proceeds. A raise cannot open without one.',
      },
      {
        to: '/admin/distributions',
        label: 'Investor distributions',
        description: 'Confirm what the SPV actually paid each investor after settlement.',
      },
    ],
  },
  {
    title: 'Operations',
    blurb: 'Day to day support and account administration.',
    tiles: [
      {
        to: '/admin/orders',
        label: 'Order and ticket support',
        description: 'Look up an order, refund it, void or reissue tickets.',
      },
      {
        to: '/admin/organizations',
        label: 'Organizations',
        description: 'Tenants, their venues, and who belongs to them.',
      },
      {
        to: '/admin/spaces',
        label: 'Spaces',
        description: 'The rooms inside each venue, and the policy each one carries.',
      },
      {
        to: '/admin/users',
        label: 'Users',
        description: 'Search accounts, change roles, suspend and reinstate.',
      },
    ],
  },
  {
    title: 'Records',
    blurb: 'What happened, and what it came to.',
    tiles: [
      {
        to: '/admin/financials',
        label: 'Financials',
        description: 'Revenue, fees and settlement figures across events.',
      },
      {
        to: '/admin/audit',
        label: 'Audit log',
        description: 'Every recorded action, append only.',
      },
      {
        to: '/admin/plugins',
        label: 'Plugins',
        description: 'Enable or disable installed plugins.',
      },
    ],
  },
];

export function AdminHome() {
  return (
    <div className="adm-page">
      <header className="adm-header">
        <div>
          <h1 className="adm-title">Admin console</h1>
          <p className="adm-lede">
            Every admin tool, in one place. The left nav has the same destinations once
            you know where you are going.
          </p>
        </div>
      </header>

      {GROUPS.map((group) => (
        <section className="hub-group" key={group.title}>
          <div className="hub-group__head">
            <h2 className="hub-group__title">{group.title}</h2>
            <p className="hub-group__blurb">{group.blurb}</p>
          </div>

          <ul className="hub-grid">
            {group.tiles.map((tile) => (
              <li key={tile.to}>
                <Link className="hub-tile" to={tile.to}>
                  <span className="hub-tile__label">
                    {tile.label}
                    {tile.external && <span className="hub-tile__away">Organizer</span>}
                  </span>
                  <span className="hub-tile__desc">{tile.description}</span>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}
